import type { ExpressionResult } from "../types";

class ExpressionParser {
  private position = 0;

  constructor(private readonly source: string) {}

  parse(): number {
    const value = this.parseExpression();
    this.skipWhitespace();
    if (this.position !== this.source.length) {
      throw new Error(`无法识别“${this.source[this.position]}”`);
    }
    if (!Number.isFinite(value)) {
      throw new Error("计算结果不是有限数值");
    }
    return value;
  }

  private parseExpression(): number {
    let value = this.parseTerm();
    while (true) {
      this.skipWhitespace();
      if (this.consume("+")) {
        value += this.parseTerm();
      } else if (this.consume("-")) {
        value -= this.parseTerm();
      } else {
        return value;
      }
    }
  }

  private parseTerm(): number {
    let value = this.parseUnary();
    while (true) {
      this.skipWhitespace();
      if (this.consume("*")) {
        value *= this.parseUnary();
      } else if (this.consume("/")) {
        const divisor = this.parseUnary();
        if (divisor === 0) {
          throw new Error("不能除以零");
        }
        value /= divisor;
      } else {
        return value;
      }
    }
  }

  private parseUnary(): number {
    this.skipWhitespace();
    if (this.consume("+")) return this.parseUnary();
    if (this.consume("-")) return -this.parseUnary();
    return this.parsePostfix();
  }

  private parsePostfix(): number {
    let value = this.parsePrimary();
    this.skipWhitespace();
    if (this.consume("%")) {
      value /= 100;
      this.skipWhitespace();
      if (this.peek() === "%") {
        throw new Error("百分号不能连续使用");
      }
    }
    return value;
  }

  private parsePrimary(): number {
    this.skipWhitespace();
    if (this.consume("(")) {
      const value = this.parseExpression();
      this.skipWhitespace();
      if (!this.consume(")")) {
        throw new Error("缺少右括号");
      }
      return value;
    }

    const rest = this.source.slice(this.position);
    const match = rest.match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
    if (!match) {
      if (this.position >= this.source.length) {
        throw new Error("算式还没有写完");
      }
      throw new Error(`此处需要数字或左括号`);
    }
    this.position += match[0].length;
    return Number(match[0]);
  }

  private skipWhitespace(): void {
    while (/\s/.test(this.source[this.position] ?? "")) this.position += 1;
  }

  private consume(character: string): boolean {
    if (this.source[this.position] !== character) return false;
    this.position += 1;
    return true;
  }

  private peek(): string | undefined {
    return this.source[this.position];
  }
}

export function parseExpression(raw: string): ExpressionResult {
  const source = raw.trim();
  if (!source) {
    return { ok: false, value: null, error: "请输入数值" };
  }

  try {
    const value = new ExpressionParser(source).parse();
    return { ok: true, value, error: null };
  } catch (error) {
    return {
      ok: false,
      value: null,
      error: error instanceof Error ? error.message : "算式格式不正确",
    };
  }
}

export function parsePercentageExpression(raw: string): ExpressionResult {
  const source = raw.trim();
  if (!source) {
    return { ok: true, value: 0, error: null };
  }
  if (source.includes("%")) {
    return {
      ok: false,
      value: null,
      error: "请直接输入百分点，例如20代表20%",
    };
  }

  const result = parseExpression(source);
  if (!result.ok || result.value === null) return result;
  return { ok: true, value: result.value / 100, error: null };
}

export function appendPercentagePointStep(raw: string, points: number): string {
  const source = raw.trim();
  if (!source) return String(points);
  const sign = points >= 0 ? "+" : "-";
  return `${source}${sign}${Math.abs(points)}`;
}
